import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { Loader2, Send, MessageSquare, Home, RefreshCw, Camera, ImagePlus, FileText, X, Calendar, Video, Paperclip } from "lucide-react";

const firstName = (full) => (full || "").trim().split(/\s+/)[0] || "";

export default function CustomerCase() {
    const { token } = useParams();
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [chatAttachments, setChatAttachments] = useState([]); // [{id, filename, ext, content_type, localUrl}]
    const [chatUploading, setChatUploading] = useState(false);
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);
    const chatAttachRef = useRef(null);
    const messagesEndRef = useRef(null);

    const load = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await api.get(`/public/cases/${token}`);
            setCaseData(data);
            setNotFound(false);
        } catch (e) {
            if (e.response?.status === 404) setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [token]);

    useEffect(() => {
        if (caseData?.messages?.length) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [caseData?.messages?.length]);

    const send = async () => {
        const trimmed = text.trim();
        if (!trimmed && chatAttachments.length === 0) return;
        setSending(true);
        try {
            const { data } = await api.post(`/public/cases/${token}/messages`, {
                text: trimmed,
                attachment_ids: chatAttachments.map((a) => a.id),
            });
            setCaseData(data);
            setText("");
            setChatAttachments([]);
            toast.success("Beskeden er sendt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke sende beskeden");
        } finally {
            setSending(false);
        }
    };

    const uploadChatAttachments = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setChatUploading(true);
        try {
            for (const file of files) {
                const ext = file.name.split(".").pop()?.toLowerCase() || "";
                if (!["jpg", "jpeg", "png", "heic", "heif", "webp", "pdf"].includes(ext)) {
                    toast.error(`${file.name}: filtype ikke tilladt`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", file);
                try {
                    const { data } = await api.post(`/public/cases/${token}/message-files`, fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    setChatAttachments((prev) => [
                        ...prev,
                        {
                            id: data.id,
                            filename: data.filename,
                            ext: data.ext,
                            content_type: data.content_type,
                            localUrl: URL.createObjectURL(file),
                        },
                    ]);
                } catch (e) {
                    toast.error(`${file.name}: ${e.response?.data?.detail || "upload fejlede"}`);
                }
            }
        } finally {
            setChatUploading(false);
        }
    };

    const removeChatAttachment = (id) => {
        setChatAttachments((prev) => {
            const target = prev.find((a) => a.id === id);
            if (target?.localUrl) URL.revokeObjectURL(target.localUrl);
            return prev.filter((a) => a.id !== id);
        });
    };

    const upload = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setUploading(true);
        let success = 0;
        try {
            for (const file of files) {
                const ext = file.name.split(".").pop()?.toLowerCase() || "";
                if (!["jpg", "jpeg", "png", "heic", "heif", "webp", "pdf"].includes(ext)) {
                    toast.error(`${file.name}: filtype ikke tilladt`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", file);
                try {
                    await api.post(`/public/cases/${token}/files`, fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    success++;
                } catch (e) {
                    toast.error(`${file.name}: ${e.response?.data?.detail || "upload fejlede"}`);
                }
            }
            if (success > 0) {
                toast.success(`${success} fil${success > 1 ? "er" : ""} uploadet`);
                await load(true);
            }
        } finally {
            setUploading(false);
        }
    };

    const fileUrl = (fid) => `${API}/public/cases/${token}/files/${fid}`;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#78736E]" />
            </div>
        );
    }

    if (notFound || !caseData) {
        return (
            <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center px-6">
                    <div className="text-center">
                        <h1 className="font-heading text-2xl font-medium text-[#2C2A29] mb-2">Sag ikke fundet</h1>
                        <p className="text-sm text-[#78736E] mb-6">Linket er muligvis udløbet eller forkert.</p>
                        <Link to="/">
                            <Button variant="outline" className="border-[#D2C9B9]"><Home className="w-4 h-4 mr-2" />Til forsiden</Button>
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAF9]" data-testid="customer-case-page">
            <Header />
            <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
                {/* Case header */}
                <div className="bg-white border border-[#E5E1DA] rounded-2xl p-6 sm:p-8 shadow-[0_8px_32px_rgba(44,42,41,0.04)] mb-6">
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#78736E]">Sagsnummer</div>
                            <h1 className="font-heading text-3xl font-medium text-[#2C2A29] mt-1" data-testid="case-number">
                                {caseData.case_number}
                            </h1>
                            <div className="text-sm text-[#78736E] mt-1">
                                Oprettet {(caseData.created_at || "").slice(0, 10)}
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <StatusBadge status={caseData.status} />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => load(true)}
                                className="text-[#78736E] hover:bg-[#F0EBE1] text-xs h-8"
                                data-testid="refresh-button"
                            >
                                <RefreshCw className="w-3 h-3 mr-1.5" />
                                Opdater
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-3 text-sm border-t border-[#E5E1DA] pt-5">
                        <Row k="Kategori" v={caseData.problem_category_label} />
                        <Row k="Produkt(er)" v={caseData.products?.map((p, i) => (
                            <span key={i} className="block">
                                {p.product_type}{p.room ? ` · ${p.room}` : ""}{p.color_model ? ` · ${p.color_model}` : ""} · {p.quantity} stk.
                            </span>
                        ))} />
                        <Row k="Din beskrivelse" v={caseData.problem_description} pre />
                    </div>
                </div>

                {/* Files / images */}
                <div className="bg-white border border-[#E5E1DA] rounded-2xl shadow-[0_8px_32px_rgba(44,42,41,0.04)] overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-[#E5E1DA] flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <ImagePlus className="w-4 h-4 text-[#4A4641]" />
                            <h2 className="font-heading text-base font-medium text-[#2C2A29]">Billeder og filer</h2>
                            <span className="text-xs text-[#78736E]">({caseData.file_ids?.length || 0})</span>
                        </div>
                        <div className="flex gap-2">
                            <input
                                ref={cameraRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                data-testid="customer-camera-input"
                                onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                            />
                            <input
                                ref={galleryRef}
                                type="file"
                                accept="image/*,video/*,.heic,.heif,application/pdf"
                                multiple
                                className="hidden"
                                data-testid="customer-gallery-input"
                                onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => cameraRef.current?.click()}
                                disabled={uploading}
                                data-testid="customer-camera-button"
                                className="border-[#D2C9B9] hover:bg-[#F0EBE1]"
                            >
                                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                <span className="ml-1.5 text-xs">Kamera</span>
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => galleryRef.current?.click()}
                                disabled={uploading}
                                data-testid="customer-gallery-button"
                                className="border-[#D2C9B9] hover:bg-[#F0EBE1]"
                            >
                                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                                <span className="ml-1.5 text-xs">Tilføj fil</span>
                            </Button>
                        </div>
                    </div>
                    <div className="p-6">
                        {(!caseData.file_ids || caseData.file_ids.length === 0) ? (
                            <div className="text-center py-6">
                                <ImagePlus className="w-8 h-8 text-[#D2C9B9] mx-auto mb-2" />
                                <div className="text-sm text-[#5C5854]">Ingen billeder endnu</div>
                                <div className="text-xs text-[#78736E] mt-1">Tilføj billeder så vi hurtigere kan hjælpe dig</div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                {caseData.file_ids.map((fid) => {
                                    const meta = (caseData.files || []).find((f) => f.id === fid) || {};
                                    const isVideo = (meta.content_type || "").startsWith("video/") || ["mp4","mov","m4v","webm","3gp","avi","mkv","qt"].includes(meta.ext);
                                    const isPdf = meta.ext === "pdf" || (meta.content_type || "").includes("pdf");
                                    return (
                                        <a
                                            key={fid}
                                            href={fileUrl(fid)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="aspect-square rounded-lg overflow-hidden border border-[#E5E1DA] bg-[#F4F2EF] hover:-translate-y-px hover:shadow-md transition-all flex items-center justify-center"
                                            data-testid={`customer-file-${fid}`}
                                        >
                                            {isVideo ? (
                                                <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center bg-[#2C2A29]">
                                                    <Video className="w-7 h-7 text-white mb-1" />
                                                    <div className="text-[10px] text-white/80 truncate w-full">{meta.filename || "Video"}</div>
                                                </div>
                                            ) : isPdf ? (
                                                <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                                                    <FileText className="w-7 h-7 text-[#78736E] mb-1" />
                                                    <div className="text-[10px] text-[#78736E] truncate w-full">{meta.filename || "PDF"}</div>
                                                </div>
                                            ) : (
                                                <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover" />
                                            )}
                                        </a>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Messages */}
                <div className="bg-white border border-[#E5E1DA] rounded-2xl shadow-[0_8px_32px_rgba(44,42,41,0.04)] overflow-hidden">
                    <div className="px-6 py-4 border-b border-[#E5E1DA] flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-[#4A4641]" />
                        <h2 className="font-heading text-base font-medium text-[#2C2A29]">Samtale med Gardinbussen</h2>
                    </div>

                    <div className="px-6 py-6 max-h-[480px] overflow-y-auto bg-[#FAFAF9]">
                        {(!caseData.messages || caseData.messages.length === 0) && (
                            <div className="text-center py-12">
                                <div className="w-12 h-12 rounded-full bg-[#F0EBE1] mx-auto flex items-center justify-center mb-3">
                                    <MessageSquare className="w-5 h-5 text-[#4A4641]" />
                                </div>
                                <div className="text-sm text-[#5C5854]">Ingen beskeder endnu</div>
                                <div className="text-xs text-[#78736E] mt-1">Skriv en besked nedenfor – vi svarer hurtigst muligt</div>
                            </div>
                        )}

                        <AnimatePresence initial={false}>
                            {caseData.messages?.map((m) => (
                                <motion.div
                                    key={m.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className={`flex mb-3 ${m.sender_type === "customer" ? "justify-end" : "justify-start"}`}
                                    data-testid={`message-${m.id}`}
                                >
                                    <div
                                        className={`max-w-[78%] rounded-2xl px-4 py-3 ${
                                            m.sender_type === "customer"
                                                ? "bg-[color:var(--brand-primary)] text-white rounded-br-sm"
                                                : "bg-white border border-[#E5E1DA] text-[#2C2A29] rounded-bl-sm"
                                        }`}
                                    >
                                        <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${m.sender_type === "customer" ? "text-white/60" : "text-[#78736E]"}`}>
                                            {m.sender_type === "customer" ? "Dig" : `${firstName(m.sender_name)} · Gardinbussen`}
                                        </div>
                                        <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                                        {m.attachment_ids && m.attachment_ids.length > 0 && (
                                            <div className={`mt-2 grid gap-2 ${m.attachment_ids.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                                                {m.attachment_ids.map((fid) => {
                                                    const meta = (caseData.files || []).find((f) => f.id === fid) || {};
                                                    const isImage = (meta.content_type || "").startsWith("image/") || ["jpg","jpeg","png","heic","heif","webp"].includes(meta.ext);
                                                    const isPdf = meta.ext === "pdf" || (meta.content_type || "").includes("pdf");
                                                    return (
                                                        <a
                                                            key={fid}
                                                            href={fileUrl(fid)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className={`block rounded-lg overflow-hidden border ${m.sender_type === "customer" ? "border-white/20" : "border-[#E5E1DA]"} bg-black/5 hover:opacity-90 transition-opacity`}
                                                            data-testid={`message-attachment-${fid}`}
                                                        >
                                                            {isImage ? (
                                                                <img src={fileUrl(fid)} alt={meta.filename || "vedhæftning"} className="w-full max-h-56 object-cover" />
                                                            ) : isPdf ? (
                                                                <div className={`flex items-center gap-2 p-3 text-xs ${m.sender_type === "customer" ? "text-white/90" : "text-[#2C2A29]"}`}>
                                                                    <FileText className="w-4 h-4 shrink-0" />
                                                                    <span className="truncate">{meta.filename || "PDF"}</span>
                                                                </div>
                                                            ) : (
                                                                <div className={`flex items-center gap-2 p-3 text-xs ${m.sender_type === "customer" ? "text-white/90" : "text-[#2C2A29]"}`}>
                                                                    <Paperclip className="w-4 h-4 shrink-0" />
                                                                    <span className="truncate">{meta.filename || "Vedhæftning"}</span>
                                                                </div>
                                                            )}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {m.cta && m.cta.url && (
                                            <a
                                                href={m.cta.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#993C3C] hover:bg-[#7E2F2F] text-white text-sm font-semibold transition-colors"
                                                data-testid={`customer-message-cta-${m.id}`}
                                            >
                                                <Calendar className="w-4 h-4" />
                                                {m.cta.label || "Book en tid"}
                                            </a>
                                        )}
                                        <div className={`text-[10px] mt-1.5 ${m.sender_type === "customer" ? "text-white/50" : "text-[#78736E]"}`}>
                                            {(m.created_at || "").slice(0, 16).replace("T", " ")}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="border-t border-[#E5E1DA] p-4 bg-white">
                        {chatAttachments.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-2" data-testid="chat-attachment-previews">
                                {chatAttachments.map((a) => {
                                    const isImage = (a.content_type || "").startsWith("image/") || ["jpg","jpeg","png","heic","heif","webp"].includes(a.ext);
                                    return (
                                        <div
                                            key={a.id}
                                            className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E1DA] bg-[#F4F2EF] group"
                                            data-testid={`chat-attachment-preview-${a.id}`}
                                        >
                                            {isImage ? (
                                                <img src={a.localUrl} alt={a.filename} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center p-1 text-center">
                                                    <FileText className="w-5 h-5 text-[#78736E]" />
                                                    <span className="text-[9px] text-[#78736E] truncate w-full mt-1">{a.filename}</span>
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeChatAttachment(a.id)}
                                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#2C2A29]/70 text-white flex items-center justify-center hover:bg-[#2C2A29]"
                                                data-testid={`remove-chat-attachment-${a.id}`}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <Textarea
                            rows={3}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Skriv en besked til kundeservice..."
                            disabled={sending}
                            data-testid="message-input"
                            className="resize-none border-[#E5E1DA]"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                            }}
                        />
                        <input
                            ref={chatAttachRef}
                            type="file"
                            accept="image/*,.heic,.heif,application/pdf"
                            multiple
                            className="hidden"
                            data-testid="chat-attach-input"
                            onChange={(e) => { uploadChatAttachments(e.target.files); e.target.value = ""; }}
                        />
                        <div className="flex items-center justify-between mt-2 gap-2">
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => chatAttachRef.current?.click()}
                                    disabled={sending || chatUploading}
                                    data-testid="chat-attach-button"
                                    className="border-[#D2C9B9] hover:bg-[#F0EBE1] h-9"
                                >
                                    {chatUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                                    <span className="ml-1.5 text-xs">Vedhæft billede</span>
                                </Button>
                                <div className="text-xs text-[#78736E] hidden sm:block">Ctrl+Enter for at sende</div>
                            </div>
                            <Button
                                onClick={send}
                                disabled={sending || (!text.trim() && chatAttachments.length === 0)}
                                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
                                data-testid="send-message-button"
                            >
                                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                Send
                            </Button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

function Header() {
    return (
        <header className="border-b border-[#E5E1DA] bg-white">
            <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                <Logo to="/" size="sm" subtitle={null} />
                <div className="text-xs text-[#78736E] uppercase tracking-wider">Min sag</div>
            </div>
        </header>
    );
}

function Row({ k, v, pre }) {
    return (
        <div className="grid grid-cols-[140px_1fr] gap-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">{k}</div>
            <div className={`text-sm text-[#2C2A29] ${pre ? "whitespace-pre-wrap" : ""}`}>{v}</div>
        </div>
    );
}
